// SSE subscriber for mirror-agent's /api/events stream.
//
// Wire format (from web_server.mjs):
//   { type: "hello", sessionId }                            ← first frame, server-assigned id
//   { type: "ping" }                                        ← heartbeats; drop
//   { type: "agent:event", runId, event: {...}, savedSessionId? }   ← wraps the real event
//   { type: "agent:done",  ok, messages, iterations, aborted, savedSessionId? }
//   { type: "agent:approval", approvalId, toolName, toolArgs, dangerous? }
//   { type: "agent:approval_expired", approvalId }
//   { type: "agent:ask_user", questionId, question, options }
//   { type: "queue:updated", ... }
//   { type: "alarm:schedule" } / { type: "alarm:cancel" }   ← user-scoped, not chat
//
// This module unwraps agent:event so consumers see a clean event-typed
// stream (token / tool_call / tool_result / etc.) and routes the
// top-level lifecycle events (done / approval / ask_user) through with
// their original shape.

import { browserSessionId } from "./rpc";

export interface AgentEvent {
  type: string;
  runId?: string;
  savedSessionId?: string;
  [k: string]: unknown;
}

export interface TokenEvent extends AgentEvent {
  type: "token";
  text: string;
}

export interface AgentDoneEvent extends AgentEvent {
  type: "agent:done";
  ok: boolean;
  messages?: unknown[];
  iterations?: number;
  aborted?: boolean;
}

export interface ApprovalEvent extends AgentEvent {
  type: "agent:approval";
  approvalId: string;
  toolName: string;
  toolArgs: unknown;
  dangerous?: boolean;
  dangerousReason?: string;
}

export interface AskUserEvent extends AgentEvent {
  type: "agent:ask_user";
  questionId: string;
  question: string;
  options?: { label: string; description?: string }[];
}

type Listener = (ev: AgentEvent) => void;

// How long the client will tolerate silence before declaring the
// EventSource stalled and force-reconnecting. Paired with the server's
// 10s keepalive cadence — two missed pings is plenty of evidence the
// pipe is dead, while still being short enough that a user who hits
// send after a quiet minute doesn't watch their message hang. The
// failure mode without this watchdog: CF Tunnel (or any proxy with a
// short idle timeout) silently drops the SSE socket, the browser's
// EventSource stays in readyState=OPEN with no onerror, and the next
// run.start's tokens are written into a closed pipe — UI never sees
// them, user has to manually refresh.
const SSE_STALL_THRESHOLD_MS = 20_000;
const SSE_STALL_CHECK_INTERVAL_MS = 5_000;

class SseManager {
  private es: EventSource | null = null;
  private listeners = new Set<Listener>();
  private lastSeq = 0;
  private serverSessionId: string | null = null;
  private visibilityHooked = false;
  /** ms epoch of the last byte we received on the open EventSource.
   *  Bumped on every onmessage (including pings), reset on (re)open. */
  private lastDataAt = 0;
  private stallTimer: ReturnType<typeof setInterval> | null = null;

  /** Server-assigned id from the most recent `hello`, if any. */
  sessionId(): string | null { return this.serverSessionId; }

  start() {
    if (this.es) return;
    const clientId = browserSessionId();
    const id = this.serverSessionId || clientId;
    const params = new URLSearchParams();
    params.set("session", id);
    if (this.lastSeq > 0) params.set("lastEventSeq", String(this.lastSeq));
    const url = `/api/events?${params}`;
    const es = new EventSource(url, { withCredentials: true });
    this.lastDataAt = Date.now();
    es.onmessage = (e) => {
      // Any data — including pings — counts as "the pipe is alive."
      this.lastDataAt = Date.now();
      // Track last server-issued event id so a reconnect can replay
      // from where we left off.
      if (e.lastEventId) {
        const n = Number(e.lastEventId);
        if (Number.isFinite(n) && n > this.lastSeq) this.lastSeq = n;
      }
      let msg: any;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (!msg || typeof msg.type !== "string") return;

      // Drop heartbeats — they exist to keep proxies alive, not for UI.
      // (We already bumped lastDataAt above; that's the whole point.)
      if (msg.type === "ping") return;

      // hello carries the server's authoritative sessionId. Subsequent
      // RPC calls should already be using this (it matches what the
      // server registered the SSE connection under), but capturing it
      // also lets future reconnects use the right id.
      if (msg.type === "hello") {
        if (typeof msg.sessionId === "string") this.serverSessionId = msg.sessionId;
        return;
      }

      // agent:event wraps the actual event (token / tool_call / etc.).
      // Forward the inner event with runId + savedSessionId stamped
      // through so listeners can filter if they need to.
      if (msg.type === "agent:event" && msg.event && typeof msg.event.type === "string") {
        const out: AgentEvent = {
          ...msg.event,
          runId: msg.runId,
          savedSessionId: msg.savedSessionId,
        };
        this.fanout(out);
        return;
      }

      // Lifecycle events come through at the top level already shaped
      // the way listeners want them.
      this.fanout(msg as AgentEvent);
    };
    es.onerror = () => {
      // EventSource auto-reconnects. lastEventSeq replays anything we
      // missed. If the connection genuinely dies, the browser keeps
      // trying — no manual retry loop needed for v1.
    };
    this.es = es;
    this.hookVisibilityReconnect();
    this.startStallWatchdog();
  }

  /**
   * Polls every SSE_STALL_CHECK_INTERVAL_MS — if no data has arrived
   * in SSE_STALL_THRESHOLD_MS, close + reopen. Picks up CF Tunnel /
   * proxy / NAT idle-timeout silent drops the browser's EventSource
   * doesn't notice (readyState stays OPEN, onerror never fires).
   *
   * Reconnect is safe: the server's lastEventSeq replay catches us
   * up on anything we missed during the stall window.
   */
  private startStallWatchdog() {
    if (this.stallTimer) return;
    this.stallTimer = setInterval(() => {
      if (!this.es) return;
      const idle = Date.now() - this.lastDataAt;
      if (idle < SSE_STALL_THRESHOLD_MS) return;
      console.warn(`[sse] stall (${idle}ms idle) — force-reconnecting`);
      try { this.es.close(); } catch {}
      this.es = null;
      this.start();
    }, SSE_STALL_CHECK_INTERVAL_MS);
  }

  /**
   * On a backgrounded tab, mobile browsers (and Chrome desktop on
   * laptops with energy-saving on) throttle EventSource — the
   * connection can sit in a zombie state where the socket is "open"
   * to the OS but nothing flows, AND onerror never fires. When the
   * user tabs back, they see stale data until the next live event.
   *
   * This hook fires once on first start(); on every visibility-
   * visible transition it tears down + reopens the connection.
   * The server-side replay handles any events missed in between
   * via the `lastEventSeq` query param we always pass.
   */
  private hookVisibilityReconnect() {
    if (this.visibilityHooked || typeof document === "undefined") return;
    this.visibilityHooked = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      // Only reconnect if we previously had a connection; if start()
      // hasn't run there's nothing to bounce.
      if (!this.es) return;
      // Skip the bounce when the connection is fresh (just opened)
      // — readyState OPEN with no observed silence is fine. We don't
      // have a "last-data-at" timestamp readily available, so just
      // close + reopen unconditionally; the server's lastEventSeq
      // replay makes this cheap.
      try { this.es.close(); } catch {}
      this.es = null;
      this.start();
    });
  }

  stop() {
    this.es?.close();
    this.es = null;
    if (this.stallTimer) {
      clearInterval(this.stallTimer);
      this.stallTimer = null;
    }
  }

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    this.start();
    return () => {
      this.listeners.delete(fn);
      if (this.listeners.size === 0) this.stop();
    };
  }

  private fanout(ev: AgentEvent) {
    for (const fn of this.listeners) {
      try { fn(ev); }
      catch (err) { console.warn("[sse] listener threw", err); }
    }
  }
}

export const sse = new SseManager();
